// Money — THE section for every money commitment, of both shapes:
//   · chits and borrows: money came IN, repaid on a schedule until cleared
//   · salary / rent / EMI: a bill that goes out every week or month, no end
// Chit and Planned Payments used to be two screens modelling the same money two
// ways; they are merged here. Paying anything creates a real Expense, so it
// shows in that day's expenses and the balance in hand.
// Component-local state (no redux).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTrendingUp,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiAlertTriangle,
  FiCalendar,
  FiCheck,
  FiChevronRight,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert } from '../components/common/index.js';
import { moneyApi } from '../api/moneyApi';
import MoneyForm from '../components/money/MoneyForm.jsx';

const fmt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-IN') : value || '0';
};


// ── Progress bar ──────────────────────────────────────────────────────────────
const ProgressBar = ({ pct, done }) => {
  const value = Math.max(0, Math.min(Number(pct) || 0, 100));
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full transition-all ${done ? 'bg-emerald-500' : 'bg-violet-500'}`} style={{ width: `${value}%` }} />
    </div>
  );
};

// ── One money card ────────────────────────────────────────────────────────────
// A repeating bill has no principal to run down, so it shows THIS period's
// state instead of a progress bar.
const InvestmentCard = ({ row, onOpen }) => {
  const recurring = row.kind === 'recurring';
  return (
    <button onClick={onOpen} className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-sm font-bold text-slate-900">{row.name}</p>
            {row.category_label && (
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${recurring ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-violet-200 bg-violet-50 text-violet-700'}`}>
                {row.category_label}
              </span>
            )}
            {row.status === 'closed' && (
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">Closed</span>
            )}
            {row.fully_paid && row.status !== 'closed' && (
              <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-600">Fully paid</span>
            )}
          </div>
          {row.source && <p className="truncate text-xs text-slate-400">{row.source}</p>}
          <p className="mt-0.5 truncate text-xs font-medium text-slate-500">{row.schedule_text}</p>
        </div>
        <FiChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
      </div>

      {recurring ? (
        // A bill: what matters is whether THIS period is settled.
        <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
          <span className="text-xs font-semibold text-slate-600">
            ₹{fmt(row.period_amount)} · {row.current_period_label}
          </span>
          {row.paid_this_period ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-600">
              <FiCheck className="h-3 w-3" /> Paid
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-600">
              To pay
            </span>
          )}
        </div>
      ) : (
        <div className="mt-3">
          <div className="mb-1 flex items-baseline justify-between text-xs">
            <span className="font-semibold text-slate-600">Paid ₹{fmt(row.paid)}</span>
            <span className="text-slate-400">of ₹{fmt(row.principal)} ({Math.round(Number(row.progress_pct) || 0)}%)</span>
          </div>
          <ProgressBar pct={row.progress_pct} done={row.fully_paid} />
          {row.received_amount && (
            <p className="mt-1 text-[11px] font-semibold text-emerald-600">
              ₹{fmt(row.received_amount)} came in
            </p>
          )}
        </div>
      )}

      {/* Footer — remaining + next-due chip */}
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-amber-600">
          {recurring
            ? <span className="text-slate-400">₹{fmt(row.paid)} paid so far</span>
            : `₹${fmt(row.remaining)} remaining`}
        </span>
        {row.overdue ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-[11px] font-bold text-rose-600">
            <FiAlertTriangle className="h-3 w-3" /> Overdue{row.next_due_display ? ` · ${row.next_due_display}` : ''}
          </span>
        ) : row.next_due_display ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-500">
            <FiCalendar className="h-3 w-3" /> Next: {row.next_due_display}
          </span>
        ) : null}
      </div>
    </button>
  );
};

const MoneyInvestmentsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await moneyApi.getList({ status, search: search.trim() || undefined });
      setData(data);
      setError('');
    } catch {
      setError('Could not load money investments');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => { load(); }, [load]);

  const add = async (body) => {
    setBusy(true);
    try { await moneyApi.create(body); setAdding(false); await load(); }
    catch (e) { setError(e?.response?.data?.error || 'Could not add the investment'); }
    finally { setBusy(false); }
  };

  const rows = data?.investments || [];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-violet-700">
              <FiTrendingUp className="h-3.5 w-3.5" /> Money
            </div>
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Money</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Every money commitment in one place — chits and borrows received and repaid until
              cleared, plus salary, rent and EMI that go out every week or month. Paying anything
              here lands in that day's expenses.
            </p>
          </div>
          <button onClick={load} disabled={loading}
            className="flex items-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:opacity-50">
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        {/* Summary cards */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Total principal</p>
            <p className="mt-1 text-2xl font-black text-slate-900">₹{fmt(data?.total_principal)}</p>
            <p className="text-[11px] text-slate-400">{data?.total ?? 0} investment{Number(data?.total) === 1 ? '' : 's'}</p>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-white px-5 py-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Paid back</p>
            <p className="mt-1 text-2xl font-black text-emerald-600">₹{fmt(data?.total_paid)}</p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-white px-5 py-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Outstanding</p>
            <p className="mt-1 text-2xl font-black text-amber-600">₹{fmt(data?.total_outstanding)}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Overdue</p>
            <p className="mt-1 text-2xl font-black text-rose-600">{rows.filter((r) => r.overdue).length}</p>
            <p className="text-[11px] text-slate-400">need attention</p>
          </div>
        </div>

        {/* Add */}
        <div>
          <button onClick={() => setAdding((v) => !v)}
            className="mb-4 flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700">
            <FiPlus className="h-4 w-4" /> {adding ? 'Close' : 'Add investment'}
          </button>
          {adding && <MoneyForm onSave={add} onCancel={() => setAdding(false)} busy={busy} />}
        </div>

        {/* Filter + search */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
            {[{ v: 'active', l: 'Active' }, { v: 'closed', l: 'Closed' }, { v: 'all', l: 'All' }].map((f) => (
              <button key={f.v} onClick={() => setStatus(f.v)}
                className={`rounded-md px-3 py-1.5 text-xs font-bold transition ${status === f.v ? 'bg-violet-600 text-white' : 'text-slate-500'}`}>
                {f.l}
              </button>
            ))}
          </div>
          <div className="relative min-w-0 flex-1 sm:max-w-xs">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or source…"
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-200" />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-16 text-center">
            <FiTrendingUp className="mb-3 h-8 w-8 text-slate-300" />
            <p className="font-semibold text-slate-700">No investments yet</p>
            <p className="mt-1 text-sm text-slate-500">Add a chit fund or a private borrow to start tracking repayments.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((row) => (
              <InvestmentCard key={row.id} row={row} onOpen={() => navigate(`/money-investments/${row.id}`)} />
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MoneyInvestmentsPage;
